/**
 * Чем считать устройство.
 *
 * Здесь нет ничего, чем можно отличить одного человека от другого: версия системы,
 * телефон это или планшет, широкий экран или узкий. Так выглядят «десять установок
 * на iOS 17», а не «вот этот телефон».
 *
 * Отпечатка браузера мы не снимаем намеренно: список шрифтов, `canvas`, часовой пояс
 * и прочее, чем обычно узнают устройство, здесь не собирается и не будет. Строка
 * браузера читается ровно ради двух чисел — версии системы и класса устройства.
 */
export interface DeviceInfo {
  platform: 'web';
  app_version: string;
  os_version: string;
  device_class: string;
  screen_class: string;
}

/** Границы те же, что у `WindowSizeClass` в Android: одни цифры для обеих версий. */
const MEDIUM_WIDTH = 600;
const EXPANDED_WIDTH = 840;

export function deviceInfo(version: string): DeviceInfo {
  const width = window.innerWidth;

  return {
    platform: 'web',
    app_version: safe(version),
    os_version: safe(osVersion(navigator.userAgent)),
    device_class: deviceClass(width),
    screen_class: width < MEDIUM_WIDTH ? 'compact' : width < EXPANDED_WIDTH ? 'medium' : 'expanded',
  };
}

/**
 * Версия системы: `iOS_17.4`, `Android_14`, `macOS`. Не разобрали — `other`.
 *
 * Ради неё одной и читается строка браузера. Без версии iOS непонятно, скольких
 * касается ветка IndexedDB ([D-50](docs/DECISIONS.md)): на iOS 16 база живёт в ней,
 * с iOS 17 — в OPFS, и это разные приложения по надёжности.
 */
function osVersion(agent: string): string {
  const ios = /(?:iPhone|iPad|CPU) OS (\d+)[._](\d+)/.exec(agent);
  if (ios) return `iOS_${ios[1]}.${ios[2]}`;

  const android = /Android (\d+)(?:\.(\d+))?/.exec(agent);
  if (android) return `Android_${android[1]}`;

  // Mac с сенсорным экраном — это iPad: с iPadOS 13 он представляется настольным
  // Safari, и без этой проверки все планшеты считались бы настольными машинами.
  if (/Macintosh/.test(agent)) return navigator.maxTouchPoints > 1 ? 'iPadOS' : 'macOS';
  if (/Windows/.test(agent)) return 'Windows';

  return 'other';
}

function deviceClass(width: number): string {
  if (!/Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent) && navigator.maxTouchPoints < 2) {
    return 'desktop';
  }
  return width < MEDIUM_WIDTH ? 'phone' : 'tablet';
}

/**
 * Сервер принимает только короткие строки без сюрпризов (`SAFE_TEXT` в `service.py`).
 * Всё остальное он молча отбрасывает — лучше прислать `other`, чем потерять поле.
 */
const safe = (value: string): string => (/^[0-9A-Za-z._-]{1,32}$/.test(value) ? value : 'other');
